// put style here
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { CSSProperties, Styles } from '@material-ui/styles';
import { fontFamily } from 'html2canvas/dist/types/css/property-descriptors/font-family';
import { fontWeight } from 'html2canvas/dist/types/css/property-descriptors/font-weight';

export const NAVY = '#23296F';
export const TEXT = '#0E1247';
export const MUTED = '#8B90A5';
export const BORDER = '#D5D8E5';

export type AdminPagesSharedClassKey =
  | 'page'
  | 'backLink'
  | 'card'
  | 'title'
  | 'stepLabel'
  | 'progressTrack'
  | 'progressFill'
  | 'sectionTitle'
  | 'field'
  | 'label'
  | 'labelRow'
  | 'optional'
  | 'textField'
  | 'placeholder'
  | 'serviceChip'
  | 'infoBox'
  | 'primaryBtn'
  | 'secondaryBtn'
  | 'resourceSection'
  | 'resourceHeader'
  | 'searchWrap'
  | 'searchFieldWrap'
  | 'selectedList'
  | 'selectedRow'
  | 'selectedLabel'
  | 'iconBtn'
  | 'dropdown'
  | 'dropdownGroup'
  | 'dropdownGroupSubtitle'
  | 'dropdownItem'
  | 'emptyHint'
  | 'loader'
  | 'postalVilleRow'
  | 'phoneField'
  | 'statusTag'
  | 'statusDone'
  | 'statusTodo'
  | 'statusProgress';

/** Shared styles for AddPrestation, BeneficiaryProfile, CreateBeneficiary and EditPrestation. */
export const adminPagesSharedStyles: Record<AdminPagesSharedClassKey, CSSProperties> = {
  page: {
    minHeight: 'calc(100vh - 80px)',
    width: '94%',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: 40,
    marginBottom: 100,
    '@media (max-width: 600px)': {
      padding: '16px 12px 40px',
    },
  },
  backLink: {
    display: 'inline-flex' as const,
    alignItems: 'center',
    gap: 6,
    marginBottom: 16,
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: '#383838',
    fontFamily: 'Poppins',
    fontSize: 15,
    fontWeight: 500,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  card: {
    margin: '0 auto',
    background: '#FFFFFF',
    borderRadius: 12,
    boxShadow: '0 2px 12px rgba(14, 18, 71, 0.06)',
    padding: '40px 48px 32px',
    display: 'flex' as const,
    flexDirection: 'column' as const,
    '@media (max-width: 600px)': {
      padding: '24px 16px 20px',
    },
  },
  title: {
    fontFamily: 'Poppins',
    fontSize: 32,
    fontWeight: 500,
    color: '#000B33',
    margin: 0,
    letterSpacing: '-0.02em',
  },
  stepLabel: {
    fontFamily: 'Poppins',
    fontSize: 14,
    color: MUTED,
    margin: '10px 0 12px',
  },
  progressTrack: {
    width: '100%',
    height: 6,
    borderRadius: 6,
    background: '#E8EAF0',
    overflow: 'hidden',
    marginBottom: 36,
  },
  progressFill: {
    height: '100%',
    borderRadius: 6,
    background: NAVY,
    transition: 'width 0.25s ease',
  },
  sectionTitle: {
    fontFamily: 'Poppins',
    fontSize: 20,
    fontWeight: 500,
    color: '#333333',
    margin: '0 0 22px',
  },
  field: {
    marginBottom: 20,
  },
  label: {
    display: 'block',
    fontFamily: 'Poppins',
    fontSize: 14,
    fontWeight: 600,
    color: TEXT,
    marginBottom: 8,
  },
  labelRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: 8,
    '& $label': {
      marginBottom: 0,
    },
  },
  optional: {
    fontFamily: 'Poppins',
    fontSize: 13,
    fontWeight: 400,
    color: MUTED,
  },
  textField: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      fontFamily: 'Poppins',
      fontSize: 14,
      minHeight: 48,
      background: '#FFFFFF',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: BORDER,
    },
    '& .MuiOutlinedInput-input': {
      padding: '13px 14px',
    },
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      minHeight: 22,
    },
    '& span': {
      fontFamily: 'Poppins',
    },
    '& input': {
      fontFamily: 'Poppins',
    },
    '& input::placeholder': {
      fontFamily: 'Poppins',
      color: '#333333',
      fontWeight: 400,
      fontSize: 14,
    },
    '& .MuiSelect-icon': {
      color: '#333333',
    },
  },
  placeholder: {
    color: MUTED,
    fontStyle: 'normal' as const,
  },
  serviceChip: {
    background: '#EEF0FA',
    color: '#000A8D',
    fontFamily: 'Poppins',
    fontWeight: 500,
    fontSize: 14,
    height: 'auto',
    minHeight: 36,
    borderRadius: 8,
    '& .MuiChip-label': {
      padding: '8px 10px 8px 14px',
      fontFamily: 'Poppins',
      fontWeight: 500,
      fontSize: 14,
      lineHeight: 1.35,
    },
    '& .MuiChip-deleteIcon': {
      color: '#000A8D',
      width: 14,
      height: 14,
      margin: '0 12px 0 2px',
      '&:hover': {
        color: '#00086F',
      },
    },
  },
  infoBox: {
    background: '#EEF0FA',
    color: NAVY,
    borderRadius: 10,
    padding: '14px 18px',
    fontFamily: 'Poppins',
    fontSize: 14,
    lineHeight: 1.55,
    marginBottom: 28,
  },
  primaryBtn: {
    background: NAVY,
    color: '#FFFFFF',
    textTransform: 'none' as const,
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 15,
    borderRadius: 8,
    padding: '11px 22px',
    minWidth: 148,
    boxShadow: 'none',
    '&:hover': {
      background: '#1b2158',
      boxShadow: 'none',
    },
    '&:disabled': {
      background: '#E8EAF0',
      color: MUTED,
    },
  },
  secondaryBtn: {
    background: '#FFFFFF',
    color: TEXT,
    border: `1.5px solid ${BORDER}`,
    textTransform: 'none' as const,
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 15,
    borderRadius: 8,
    padding: '11px 18px',
    minWidth: 148,
    boxShadow: 'none',
    '&:hover': {
      background: '#F7F8FB',
      boxShadow: 'none',
    },
  },
  resourceSection: {
    marginBottom: 28,
  },
  resourceHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontFamily: 'Poppins',
    fontSize: 18,
    fontWeight: 500,
    color: TEXT,
    marginBottom: 12,
    '& svg': {
      flexShrink: 0,
      color: TEXT,
    },
  },
  searchWrap: {
    position: 'relative' as const,
  },
  searchFieldWrap: {
    position: 'relative' as const,
    width: '100%',
    '& $textField': {
      width: '100%',
    },
  },
  selectedList: {
    marginTop: 10,
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    overflow: 'hidden',
    background: '#FFFFFF',
  },
  selectedRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 16px',
    borderBottom: `1px solid ${BORDER}`,
    fontFamily: 'Poppins',
    fontSize: 14,
    color: TEXT,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  selectedLabel: {
    flex: 1,
    minWidth: 0,
  },
  iconBtn: {
    padding: 4,
    color: MUTED,
    '&:hover': {
      color: TEXT,
      background: 'transparent',
    },
  },
  dropdown: {
    position: 'absolute' as const,
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    zIndex: 10,
    maxHeight: 260,
    overflowY: 'auto',
    background: '#FFFFFF',
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    boxShadow: '0 8px 20px rgba(14, 18, 71, 0.12)',
    padding: '8px 0',
  },
  dropdownGroup: {
    padding: '8px 14px 4px',
    fontFamily: 'Poppins',
    fontSize: 13,
    fontWeight: 700,
    color: TEXT,
  },
  dropdownGroupSubtitle: {
    display: 'block',
    marginTop: 2,
    fontFamily: 'Poppins',
    fontSize: 12,
    fontWeight: 400,
    fontStyle: 'italic',
    color: MUTED,
  },
  dropdownItem: {
    padding: '0 10px',
    '& .MuiFormControlLabel-label': {
      fontFamily: 'Poppins',
      fontSize: 14,
    },
    '& .MuiCheckbox-root': {
      color: BORDER,
    },
    '& .Mui-checked': {
      color: `${NAVY} !important`,
    },
  },
  emptyHint: {
    fontFamily: 'Poppins',
    fontSize: 13,
    color: MUTED,
    padding: '8px 14px',
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    padding: 60,
  },
  postalVilleRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 3fr',
    gap: 16,
    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr',
    },
  },
  phoneField: {
    '& > div': {
      height: 48,
      borderRadius: 8,
      borderColor: BORDER,
    },
  },
  statusTag: {
    flexShrink: 0,
    fontFamily: 'Poppins',
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 6,
    padding: '4px 10px',
    lineHeight: 1.2,
  },
  statusDone: {
    background: '#E4F7EC',
    color: '#1B7A3D',
  },
  statusTodo: {
    background: '#EEF0F4',
    color: '#6B7280',
  },
  statusProgress: {
    background: '#E4F4F2',
    color: '#1A7A72',
  },
};

export const createAdminPageStyles = <ClassKey extends string = never>(
  extra: Record<ClassKey, CSSProperties> = {} as Record<ClassKey, CSSProperties>
) =>
  makeStyles({
    ...adminPagesSharedStyles,
    ...extra,
    // MUI Styles second generic is props; {} keeps useStyles() callable without arguments.
    // eslint-disable-next-line @typescript-eslint/ban-types
  } as Styles<Theme, {}, AdminPagesSharedClassKey | ClassKey>);

export default makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: '2px 4px',
      display: 'flex',
      alignItems: 'center',
      width: 400,
    },
    input: {
      marginLeft: theme.spacing(1),
      flex: 1,
    },
    iconButton: {
      padding: 10,
    },
    divider: {
      height: 28,
      margin: 4,
    },
    formControlSelect: {
      width: '100%',
      '&>svg': {
        color: '#0E1247',
      },
      '&>fieldset': {
        borderColor: '#aaa',
      },
      '&>div': {
        paddingTop: 12,
        paddingBottom: 12,
      },
    },
    bgGrey: {
      background: '#EDEEF2',
    },
    bgWhite: {
      background: 'white',
    },
    formInput: {
      width: '100%',
      '& div fieldset': {
        borderColor: '#0E1247',
      },
      '& div input': {
        paddingTop: 12,
        paddingBottom: 12,
      },
    },
    btnWhite: {
      backgroundColor: '#fff#',
      color: '#0E1247',
      marginTop: 0,
      marginRight: 10,
      borderRadius: 3,
      opacity: 1,
      border: '1px solid grey',
      textTransform: 'none',
      paddingTop: 20,
      paddingBottom: 20,
      paddingLeft: 20,
      paddingRight: 20,
      [theme.breakpoints.down('sm')]: {
        padding: '1vh',
        minHeight: 48,
        height: 48,
        width: 140,
        minWidth: 130,
      },
    },
    textHeader: {
      display: 'none',
      marginLeft: 0,
      '@media(min-width: 600px)': {
        display: 'block',
      },
    },
    btnPrimary: {
      padding: '0 2.5vh',
      minHeight: 30,
      textTransform: 'none',
      background:
        'transparent linear-gradient(230deg, #FFDC27 0%, #E63C3C 50%, #000A8C 100%) 0% 0% no-repeat padding-box',
      color: '#fff',
    },
    groupBtnAbsolute: {
      position: 'absolute',
      bottom: 20,
      right: 20,
    },
    modalContent: {},
    modalTitle: {
      color: '#E63C3C',
      marginBottom: 30,
    },
    modalText: {
      fontSize: 14,
      fontFamily: 'ITC Avant Garde Gothic Std Medium',
    },
    bouttonRedirect: {
      borderRadius: '5px',
      borderWidth: '4px',
      borderStyle: 'solid',
      borderImage: 'linear-gradient(to top, #000A8C  50% , #E63C3C 0% , #FFDC27 100%) 1',
      width: '100%',
    },
    flexLine: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
    keyword: {
      marginRight: 20,
      width: 200,
      '& .MuiAutocomplete-inputRoot': {
        paddingTop: 4,
        paddingBottom: 5,
      },
      '& .MuiTextField-root': {
        border: '1px solid #CDCEDA',
        borderRadius: 4,
        height: 50,
      },
    },
    btn: {
      color: '#0E1247',
      background: '#FFFFFF',
      border: '1px solid #CDCEDA',
      borderRadius: 4,
      height: 50,
    },
    overrideMargin: {
      width: '94%',
      margin: '0 auto',
    },
    btnPrimaryC: {
      borderWidth: '2.5px',
      borderStyle: 'solid',
      borderImage: 'linear-gradient(to top, #000A8C  50% , #E63C3C 0% , #FFDC27 100%) 1',
      color: '#0E1247',
      width: '45%',
      backgroundColor: 'white',
      overflow: 'hidden',
      marginRight: 20,
    },
    btnOAMatch: {
      position: 'absolute',
      fontSize: 12,
      color: 'white',
      bottom: '0px',
      right: '0px',
      textTransform: 'none',
      height: 'auto !important',
      padding: '5px 10px',
      cursor: 'pointer',
      zIndex: 2,
    },
    logoImg: {
      width: '40%',
      height: 'auto',
      display: 'block',
      margin: 'auto',
    },
    btnConfirm: {
      width: '100%',
      color: 'white',
    },
    btnCancel: {
      width: '100%',
      border: '1px solid #000A8C',
      color: '#000A8C',
      background: 'white',
    },
    textOADialog: {
      fontFamily: 'Poppins',
      fontSize: 20,
      fontWeight: 500,
      lineHeight: '140%',
      textAlign: 'center',
      color: '#23296F',
    },
    modal: {
      '& .MuiPaper-root': {
        borderRadius: 16,
      },
    },
  })
);
